import { Badge } from "@backstage/ui";
import type { BundleHold } from "@internal/backstage-plugin-avd-common";
import { useCallback, useState } from "react";

import { BundlePage } from "../bundle/bundlePage";
import { usePlatformAdmin } from "../identity/ownership";

import { avdPageDescriptor } from "./avdPageDescriptor";
import { MyBundleHold } from "./bundleHold";
import { desiredIacCommitUrl } from "./desktopFacts";
import {
	avdActions,
	DesktopCard,
	deleteOnFor,
	deleteOnText,
	heroSentence,
	powerLine,
	useMyAvdDesktop,
	useMyAvdPower,
} from "./myDesktop";
import { reclaimText, useMyReclamation } from "./reclamation";

// Copy says "Set up" / "Remove", never "provision".
// Gating and layout: docs/app/avd-registry.md § How the page reads and gates on the registry.

/** A colleague's home for their Azure Virtual Desktop: its state, Connect and Remove, then Set up. */
export const MyAvdPage = () => {
	// One registry read serves the hero, the card and the gating; `actions` fails open.
	const desktop = useMyAvdDesktop();
	const power = useMyAvdPower(desktop.view === "ready");
	const actions = avdActions(desktop.view, desktop.loading);
	const reclaimed = useMyReclamation(
		"avd",
		desktop.view === "absent" ? desktop.runUrl : undefined,
	);
	const reclaimedText = reclaimed ? reclaimText(reclaimed) : undefined;
	// The hold control's answer decides whether the reclaim date shows.
	const [hold, setHold] = useState<{ holdUntil?: string }>();
	const onHoldChange = useCallback(
		(next: BundleHold | undefined) => setHold({ holdUntil: next?.holdUntil }),
		[],
	);
	const deleteOn = hold
		? deleteOnFor({ ...desktop, status: desktop.view, holdUntil: hold.holdUntil }, new Date())
		: desktop.deleteOn;

	// Links into the private GHE repo are admin-only: docs/app/avd-registry.md § Developer-only links.
	const { admin } = usePlatformAdmin();

	const powerNode = desktop.view === "ready" ? powerLine(power) : null;
	const meta =
		powerNode || deleteOn ? (
			<>
				{powerNode}
				{deleteOn ? <Badge>Deletes on {deleteOnText(deleteOn)} unless used</Badge> : null}
			</>
		) : undefined;

	// A removed desktop is an empty list; every other state, `unknown` included, is the one card.
	const absent = !desktop.loading && desktop.view === "absent";

	return (
		<BundlePage
			descriptor={avdPageDescriptor}
			title={heroSentence(desktop.view, desktop.loading, reclaimedText)}
			meta={meta}
			setup={actions.setup}
			emptyBody={reclaimedText}
			cards={
				absent
					? []
					: [
							<DesktopCard
								key="desktop"
								view={desktop.view}
								loading={desktop.loading}
								actions={actions}
								runUrl={admin ? desktop.runUrl : undefined}
								vmName={desktop.vmName}
								slug={desktop.slug}
								subscriptionName={desktop.subscriptionName}
								bundleType={desktop.bundleType}
								iacVersion={desktop.iacVersion}
								iacCommitUrl={admin ? desiredIacCommitUrl() : undefined}
								deleteOn={deleteOn}
								hold={
									<MyBundleHold
										bundleType="avd"
										noun="desktop"
										onHoldChange={onHoldChange}
									/>
								}
							/>,
						]
			}
		/>
	);
};
